import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DSButton } from '@hundred5/design-system';
import React from 'react';

import { Icon } from '@/features/common';
import {
  CustomTextEditor,
  EditorState,
} from '@/features/common/components/custom-text-editor';
import { InterviewDecision } from '@/queries/types';

interface EvaluationEditorProps {
  decision?: InterviewDecision;
  onDecisionChange: (decision: InterviewDecision) => void;
  content: EditorState;
  onContentChange: (comment: EditorState) => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  className?: string;
  disabled?: boolean;
  showError?: boolean;
  placeholder?: string;
}

export function EvaluationEditor({
  decision,
  onDecisionChange,
  content,
  onContentChange,
  onCancel,
  onSubmit,
  className,
  disabled = false,
  showError = false,
  placeholder = 'Provide your overall recommendation based on the interview.',
}: EvaluationEditorProps) {
  return (
    <div className={className}>
      <p className="mb-4">Should this candidate move to the next stage?*</p>
      <DecisionContainer>
        <DecisionButton
          decision={decision}
          active={decision === 'thumbs_down'}
          onClick={() => onDecisionChange('thumbs_down')}
          variant="secondary"
          disabled={disabled}
        >
          <Icon icon={regular('thumbs-down')} />
          No
        </DecisionButton>
        <DecisionButton
          decision={decision}
          active={decision === 'thumbs_up'}
          onClick={() => onDecisionChange('thumbs_up')}
          variant="secondary"
          disabled={disabled}
        >
          <Icon icon={regular('thumbs-up')} />
          Yes
        </DecisionButton>
      </DecisionContainer>
      {showError && <Error>Please add overall recommendation</Error>}
      <TextEditor
        placeholder={placeholder}
        state={content}
        onChange={onContentChange}
        disabled={disabled}
      />
      <Footer>
        {onCancel ? (
          <DSButton variant="secondary" onClick={onCancel}>
            Cancel
          </DSButton>
        ) : null}
        {onSubmit ? (
          <DSButton onClick={onSubmit}>Submit evaluation</DSButton>
        ) : null}
      </Footer>
    </div>
  );
}

const DecisionContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const DecisionButton = styled(DSButton)<{
  decision?: InterviewDecision;
  active: boolean;
}>`
  ${(props) =>
    props.active &&
    (props.decision === 'thumbs_up'
      ? `border-color: ${props.theme.colors.success[40]};
         background-color: ${props.theme.colors.success[20]};
         color: ${props.theme.colors.success[120]};`
      : props.decision === 'thumbs_down'
        ? `border-color: ${props.theme.colors.error[40]};
            background-color: ${props.theme.colors.error[20]};
            color: ${props.theme.colors.error[120]};`
        : '')}

  &:hover {
    ${(props) =>
      props.active &&
      (props.decision === 'thumbs_up'
        ? `background-color: ${props.theme.colors.success[30]} !important;`
        : props.decision === 'thumbs_down'
          ? `background-color: ${props.theme.colors.error[30]} !important;`
          : '')}
  }
`;

const Error = styled.span`
  color: ${(props) => props.theme.colors.error[100]};
  display: inline-block;
  margin-bottom: 16px;
`;

const TextEditor = styled(CustomTextEditor)`
  margin-bottom: 16px;
  min-height: 160px;
`;

const Footer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
